/**
 * P-B 蓝图→海报接缝(2026-07-20):location.state.explosive 验型 + 组图爆点派生。
 *
 * 手法照抄 VideoCreatePage 的 explosive handoff(一次性 ref 消费 + 逐字段验型,
 * 脏 state 整包丢弃不预载);console 不调 /blueprints/{id}/use —— 取用登记由 backend
 * 建任务时自动 best-effort(防双计,对齐视频口径)。
 */
import { buildReferenceContent } from "@/entities/explosive";

export interface GroupPointForm {
  title: string;
  /**
   * 参考句(**纯前端提示态,绝不上送**)—— 参考片这一位说的是什么。
   *
   * ★2026-08-13 老板拍板「统一口径」。参考片的爆点是**别人家的话**:
   * 长图那边它曾直落叠字(真印在成图上),组图这边它曾直接当 `title` 预填 ——
   * 用户不改就是拿竞品文案出图。两边现在都只把它当提示:
   *   长图 → 区块名「卖点N · 参考:xxx」(编辑器可见,永不进图)
   *   组图 → 输入框 placeholder(不填就不出这张,提交侧本来就滤空卡)
   * 套路给的是**结构**(几张、什么顺序),文字必须是用户自己的。
   */
  hint?: string;
  /** 这一张画什么(可空,引擎自己发挥)。B1-3 起在台本编辑器里可填 —— 它是组图独有、
   *  最该被看见的字段,改之前只在 chip 的 tooltip 里露一半。 */
  detail?: string;
  /**
   * 这一张在轮播里干什么(B1-2/B1-3)。
   *
   * ★UI 只产生 `"page"` 这一个显式值,含义是**用户把第 1 张的封面身份关掉了**。
   *   其余一律不写(undefined)= 按位置派生:第 1 张是封面。
   *   不产生显式 `"cover"` 的理由:封面就是第一张,两处各写一份必漂
   *   (排序之后谁是封面会说不清)。ai 侧同时支持显式 cover,那是给分步续出用的。
   */
  role?: "cover" | "page";
}

export interface PosterExplosiveHandoff {
  blueprintId: number;
  formulaName: string;
  payload: Record<string, unknown>;
  sourcePlatform?: string;
  /**
   * 爆点(唯一合法来源 = 拆解报告 `breakdown.explosivePoints`)。解析当次直达时来自当次报告;
   * 蓝图库路由时来自详情抽屉从库里取的 breakdown 存档(V21)。缺省 → 创建页爆点留空待手填。
   */
  explosivePoints?: string[];
  /** 2026-08-14「参考片在拍什么」:内容层随路由带来,仅展示 + 独立通道,不进出图指令。 */
  breakdown?: import("@/entities/explosive").ExplosiveBreakdown | null;
  /**
   * BPWB-1(2026-09-03)蓝图工作卡:有原片留存(source.rawVideoKey 有值)才给播放位。
   * 与 `BlueprintHeroCard.hasRawVideo` 同名同义;ExplosivePage 路由 state 已经带上了,
   * 这里只是补上验型这一环(逐字段验型不带上就会被 parsePosterExplosiveState 整包丢弃)。
   */
  hasRawVideo?: boolean;
}

/** location.state.explosive 逐字段验型;形状不对返回 null(整包丢弃,页面按无蓝图渲染)。 */
export function parsePosterExplosiveState(state: unknown): PosterExplosiveHandoff | null {
  if (!state || typeof state !== "object") return null;
  const ex = (state as { explosive?: unknown }).explosive;
  if (!ex || typeof ex !== "object") return null;
  const o = ex as Record<string, unknown>;
  if (typeof o.blueprintId !== "number" || !Number.isFinite(o.blueprintId)) return null;
  if (typeof o.formulaName !== "string" || !o.formulaName.trim()) return null;
  if (!o.payload || typeof o.payload !== "object" || Array.isArray(o.payload)) return null;
  const points = Array.isArray(o.explosivePoints)
    ? o.explosivePoints.filter((p): p is string => typeof p === "string" && !!p.trim())
    : undefined;
  return {
    blueprintId: o.blueprintId,
    formulaName: o.formulaName,
    payload: o.payload as Record<string, unknown>,
    sourcePlatform: typeof o.sourcePlatform === "string" ? o.sourcePlatform : undefined,
    explosivePoints: points && points.length > 0 ? points : undefined,
    // 只验「是个对象」(同 dh):ai 侧自由形状,逐键验型会把新增字段挡在门外
    ...(o.breakdown && typeof o.breakdown === "object"
      ? {
          breakdown: o.breakdown as import("@/entities/explosive").ExplosiveBreakdown,
        }
      : {}),
    // 非法值(非布尔)丢这一个字段就够,不连累整包(与 explosivePoints 过滤单项同规)
    ...(typeof o.hasRawVideo === "boolean" ? { hasRawVideo: o.hasRawVideo } : {}),
  };
}

const MAX_POINTS = 6;
const MAX_TITLE = 30;

/**
 * 爆点 → 组图标题:报告里的 explosivePoints 是**分析长句**(「前3秒用'珍宝若被强夺'制造强烈
 * 戏剧冲突，引发观众好奇」40~60 字),而组图标题要短、会进出图指令和叠字。
 *
 * <p>★2026-07-28 上机验收抓的:直接 `slice(0,30)` 会切在词中间(「…颠覆认」)。改成
 * 先在标点处断句取前半个意群,还超长才硬截并显式加「…」——让用户看得出被裁过,而不是
 * 以为爆点本来就写成半句。
 */
function pointTitle(raw: string): string {
  const s = raw.trim().replace(/^[0-9]+[.、)]\s*/, "");
  if (s.length <= MAX_TITLE) return s;
  // 在首个句读处断开(中英文逗号/分号/句号/破折号),取到长度上限内最靠后的那一段
  const cut = s.slice(0, MAX_TITLE + 1);
  const m = cut.match(/^[\s\S]*?(?=[，,。；;、—])/);
  const head = m?.[0]?.trim();
  if (head && head.length >= 8) return head;
  return s.slice(0, MAX_TITLE - 1) + "…";
}

/**
 * 蓝图 → **N 个空卖点位 + 各自的参考句**(2026-08-13 起不再产出 title)。
 *
 * 唯一合法来源 = 拆解报告的 explosivePoints(解析当次 breakdown,或蓝图详情从库里带出的
 * breakdown 存档,V21 起随时可取)。取不到就返回 [] 让用户手填。
 *
 * <p>★2026-07-28 走查 W2 删掉了两级兜底,别再加回来:
 *
 * <ul>
 *   <li>`payload.sceneCards[].beat` 是**分镜名**(「高冲突夺宝钩子」「第三危机升级」),不是卖点;
 *   <li>`payload.beats15Raw` 是**导演脚本整句**,拆出来的「爆点」长这样:
 *       「先用"我刚刚取得了一个小结果"开场，制造真实感和好奇心，结果」——
 *       还带着没替换的 {产品} 占位符、第 6 条被截断成「…或验证{」,而 6 张图预扣 1680 积分。
 * </ul>
 *
 * <p>原兜底假设 `beats15Raw` 形如「0-3s 钩子:…」靠冒号切短标签,真实数据里没那个冒号 ——
 * 于是整句进了出图指令。**宁可空着让用户填,不拿导演语言冒充卖点**。
 */
export function deriveGroupPoints(handoff: PosterExplosiveHandoff): GroupPointForm[] {
  if (!handoff.explosivePoints?.length) {
    return [];
  }
  const seen = new Set<string>();
  const points: GroupPointForm[] = [];
  for (const raw of handoff.explosivePoints) {
    const hint = pointTitle(raw);
    if (!hint || seen.has(hint)) continue;
    seen.add(hint);
    // ★2026-08-14:title 填成参考句本身(此前恒空,只当 placeholder)。
    //
    // 为什么改回来:0813 置空是为了防「用户不改就出图 = 竞品文案印进自家物料」,方向对,
    // 但代价是**从蓝图进组图永远撞 0 张态** —— 真机截图实证:5 张卡全空、底栏「将出 0 张」、
    // 按钮灰,用户看不出套路带了任何东西(用户原话:「没有显示实际传给地基的值」)。
    //
    // 现在改成「填值 + 出图前闸」:值可见、可直接改;真到点生成时,若某张仍与参考句逐字
    // 相同,`untouchedReferenceTitles` 会把它挑出来让用户确认(见 groupScript.ts)。
    // 可见性与红线两头都要 —— 只靠 placeholder 拿不到可见性,只靠填值守不住红线。
    points.push({ title: hint, hint });
    if (points.length >= MAX_POINTS) break;
  }
  return points;
}

/**
 * 蓝图 → **备料框初值**(2026-08-25 用户拍板:「蓝图的东西就等于是用户输入」)。
 *
 * ═══ 为什么落在备料框,而不是铺成区块 ═════════════════════════════════════
 * 与军师交接卡同一条路(见 longimage/model/advisorPrefillSeed.ts 的论证):
 * 长图第一步本来就是「备料」,落在框里 = 用户**看得见、能改、能清空**,
 * 之后与他自己打字进来的料**走完全同一条路**(comprehend → 编排 / 一键出图,
 * 报价也同一把 —— `longImageOneClickEstimate` 只吃用途和分辨率档,不看文本从哪来)。
 * 铺成区块等于替他跳过了这一步,而且他改的是块、不是料,两份真相。
 *
 * ═══ 红线怎么守 ═════════════════════════════════════════════════════════
 * 这段文本是**参考片的内容**,不是用户的产品。真机实测(0825):原样投进备料框,
 * comprehend 判 `rawTextKind="material"`,抽出来的 product 是参考片那家公司的
 * (400电话 / 5 条卖点 / 4 条规格全是竞品的),16 条 provenance 里 14 条 `from="text"`。
 * ★但这与「用户自己粘同一段文字」的结果**完全一样** —— 那个框本来就信任输入,
 *   所以这不是投放引入的新风险,区别只在**谁做的决定**。
 * ★所以这里走组图 0814 定下的同一套「填值 + 出图前闸」:
 *   值必须可见可改(只靠 placeholder 拿不到可见性),
 *   同时页面要说清这段是从哪来的、提醒改成自己的产品(可见性与红线两头都要)。
 */
export function blueprintPrefillText(handoff: PosterExplosiveHandoff | null): string {
  if (!handoff) return "";
  return buildReferenceContent(handoff.breakdown);
}

/**
 * 长图 2.0 追爆双通道(LI-SCREENS-2.0 WP5,设计定稿§3)—— handoff → ai `structure_ref
 * .StructureRef` 的投影。**字段名逐字对齐 ai 侧 Field alias**(改一边必须改另一边,
 * 跨仓锁见 `blueprint.test.ts`「structureRefFromHandoff · 跨仓契约」一段):
 *   blueprintId / formulaName / sourcePlatform / breakdown / explosivePoints。
 *
 * ★`breakdown` 传的是 `buildReferenceContent(handoff.breakdown)` 那段**文本**
 *   (与 {@link blueprintPrefillText} 逐字同一份产物,不是另一份装配)——ai `structure_ref.py`
 *   头注写明白了收的就是这段标记行形制文本,不是 `ExplosiveBreakdown` 对象。
 * ★不做竞品事实过滤:那是 ai 侧 `structure_ref.competitor_entity_gate` 的活
 *   (按来源判不按词面判,console 这层没有「我方事实」全集可比对)。
 */
export interface PosterExplosiveStructureRef {
  blueprintId: number;
  formulaName: string;
  sourcePlatform?: string;
  breakdown: string;
  explosivePoints?: string[];
}

export function structureRefFromHandoff(
  handoff: PosterExplosiveHandoff
): PosterExplosiveStructureRef {
  return {
    blueprintId: handoff.blueprintId,
    formulaName: handoff.formulaName,
    ...(handoff.sourcePlatform ? { sourcePlatform: handoff.sourcePlatform } : {}),
    breakdown: buildReferenceContent(handoff.breakdown),
    ...(handoff.explosivePoints?.length ? { explosivePoints: handoff.explosivePoints } : {}),
  };
}
